import type { LLM as LLMInterface, LLMTool, StreamEvent, ChatMessage } from "../../design/services";
import type { Context } from "../../design/app";
import type { FileMetadata } from "../../design/components";
import type { Config, Action, Entity, Model } from "../../design/types";
import { buildSystemPrompt } from "../../app/buildSystemPrompt";

const MAX_TOOL_ROUNDS = 10;

/**
 * Browser-side LLM client. Talks to run() via fetch + SSE.
 * Owns tools, files, and system prompt. Handles tool round-trips locally.
 */
export class LLM implements LLMInterface {
    private getRunUrl: () => string;
    private getIdToken: () => Promise<string>;
    private getContext: () => Context;
    private modelId: string;
    tools: LLMTool[];
    files: (FileMetadata | null)[] = [];

    constructor(getRunUrl: () => string, config: Config, getContext: () => Context, getIdToken: () => Promise<string>) {
        this.getRunUrl = getRunUrl;
        this.getIdToken = getIdToken;
        this.getContext = getContext;
        this.modelId = (config.anthropicModel as string) || 'claude-sonnet-4-20250514';
        this.tools = buildTools(getContext, this);
    }

    async *chat(params: {
        messages: ChatMessage[];
    }): AsyncGenerator<StreamEvent> {
        // Build system prompt from context + files
        const system = buildSystemPrompt(this.getContext(), this.files.filter((f): f is FileMetadata => f != null));
        const tools = this.tools.map(t => ({
            name: t.name,
            description: t.description,
            input_schema: t.parameters as Record<string, unknown>,
        }));

        // Extract chat messages from params (which come from Layout.tsx)
        let currentMessages = params.messages;

        for (let round = 0; round < MAX_TOOL_ROUNDS; round++) {
            const token = await this.getIdToken();
            const response = await fetch(this.getRunUrl(), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    method: 'llm.chat',
                    params: { messages: currentMessages, system, tools, model: this.modelId },
                }),
            });

            if (!response.ok) {
                const text = await response.text();
                throw new Error(text || `HTTP ${response.status}`);
            }

            // Parse SSE stream. Events are separated by blank lines (\n\n);
            // each event has `event: <type>` and `data: <json>` lines.
            const reader = response.body!.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            const contentBlocks: Array<{ type: string; [key: string]: unknown }> = [];
            let stopReason: string | null = null;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });

                const blocks = buffer.split('\n\n');
                buffer = blocks.pop()!; // Keep trailing partial block

                for (const block of blocks) {
                    let eventType = '';
                    let dataLine = '';
                    for (const line of block.split('\n')) {
                        if (line.startsWith('event: ')) eventType = line.slice(7);
                        else if (line.startsWith('data: ')) dataLine = line.slice(6);
                    }
                    if (!eventType || !dataLine) continue;
                    const data = JSON.parse(dataLine);
                    if (eventType === 'text') {
                        contentBlocks.push({ type: 'text', text: data.text });
                        yield data as StreamEvent;
                    } else if (eventType === 'tool_use') {
                        contentBlocks.push(data);
                        yield data as StreamEvent;
                    } else if (eventType === 'done') {
                        stopReason = data.stop_reason;
                        yield data as StreamEvent;
                    } else if (eventType === 'error') {
                        throw new Error(data.message);
                    }
                }
            }

            // If no tool use, we're done
            if (stopReason !== 'tool_use') return;

            // Execute tools locally
            const toolUseBlocks = contentBlocks.filter(b => b.type === 'tool_use');
            const toolResults: Array<{ type: 'tool_result'; tool_use_id: string; content: string }> = [];

            for (const toolUse of toolUseBlocks) {
                const tool = this.tools.find(t => t.name === toolUse.name);
                const result = tool
                    ? await tool.execute(toolUse.input as Record<string, unknown>)
                    : { error: `Unknown tool: ${toolUse.name}` };
                toolResults.push({
                    type: 'tool_result',
                    tool_use_id: toolUse.id as string,
                    content: JSON.stringify(result),
                });
            }

            // Build assistant content blocks for the next round
            const assistantContent = contentBlocks.map(b => {
                if (b.type === 'text') return { type: 'text' as const, text: b.text as string };
                return { type: 'tool_use' as const, id: b.id as string, name: b.name as string, input: b.input };
            });

            currentMessages = [
                ...currentMessages,
                { role: 'assistant', content: assistantContent },
                { role: 'user', content: toolResults },
            ] as ChatMessage[];
        }

        throw new Error('Tool calling exceeded maximum rounds');
    }
}

/**
 * Build the tools array. These tools execute client-side with access to React context.
 * Moved from the old AI abstract class in design/services.ts.
 */
function buildTools(getContext: () => Context, client: LLM): LLMTool[] {
    return [
        {
            name: 'console_log',
            description: 'Logs a message to the browser console.',
            parameters: {
                type: 'object',
                properties: {
                    message: { type: 'string', description: 'The message to log' }
                },
                required: ['message']
            },
            execute: (params: Record<string, unknown>) => {
                console.log('[LLM Tool]:', params.message);
                return { success: true, logged: params.message };
            }
        },
        {
            name: 'update',
            description: 'Updates a property value in the page. For file properties (image/pdf), use the file parameter with the index from attached files.',
            parameters: {
                type: 'object',
                properties: {
                    name: { type: 'string', description: 'The name of the property to update' },
                    value: { type: 'string', description: 'The new value to set' },
                    file: { type: 'integer', description: 'Index of an attached file to set (from attached files list)' }
                },
                required: ['name']
            },
            execute: (params: Record<string, unknown>) => {
                const fileIndex = params.file as number | undefined;
                let resolvedValue: unknown = params.value;

                if (fileIndex !== undefined) {
                    const fileMetadata = client.files[fileIndex];
                    if (!fileMetadata) {
                        return { error: `File index ${fileIndex} not found`, available: client.files.map((f, i) => f ? `[${i}] ${f.name}` : null).filter(Boolean) };
                    }

                    const model = getContext().page.current?.model;
                    const property = model?.properties.find((p: { name: string }) => p.name === params.name);
                    if (property) {
                        const isImage = fileMetadata.type.startsWith('image/');
                        const isPdf = fileMetadata.type === 'application/pdf';
                        if (property.type === 'image' && !isImage) {
                            return { error: `Property '${params.name}' is an image field but '${fileMetadata.name}' is ${fileMetadata.type}. Only image files are accepted.` };
                        }
                        if (property.type === 'pdf' && !isPdf) {
                            return { error: `Property '${params.name}' is a PDF field but '${fileMetadata.name}' is ${fileMetadata.type}. Only PDF files are accepted.` };
                        }
                    }

                    resolvedValue = fileMetadata;
                }

                let updated = false;
                const available: string[] = [];
                for (const e of getContext().page.current?.reactiveEntities ?? []) {
                    for (const c of e.components) {
                        if (c.name) available.push(c.name);
                        if (c.name === params.name && c.state?.[1]) {
                            c.state[1](resolvedValue);
                            updated = true;
                        }
                    }
                }
                if (!updated) return { error: `Property '${params.name}' not found`, available };
                if (fileIndex !== undefined) client.files[fileIndex] = null;
                return { success: true, name: params.name, ...(fileIndex !== undefined ? { file: 'set' } : { value: params.value }) };
            }
        },
        {
            name: 'run',
            description: 'Executes a model action by name. Actions contain code that operates on the current entity data.',
            parameters: {
                type: 'object',
                properties: {
                    action: { type: 'string', description: 'The name of the action to execute' }
                },
                required: ['action']
            },
            execute: async (params: Record<string, unknown>) => {
                const ctx = getContext();
                const pageState = ctx.page.current;
                if (!pageState?.model) return { error: 'No model loaded' };
                const { model, reactiveEntities, navigate } = pageState;

                const action = model.actions.find((a: Action) => a.name === params.action);
                if (!action) {
                    const available = model.actions.map((a: Action) => a.name);
                    return { error: `Action '${params.action}' not found`, available };
                }

                for (const re of reactiveEntities) {
                    const { id: entityId, ...data } = re.entity ?? {} as Entity;
                    try {
                        await ctx.runner.run(action, ctx, {
                            model,
                            id: entityId,
                            data,
                            navigate
                        });
                    } catch (err) {
                        return { error: err instanceof Error ? err.message : 'Action failed' };
                    }
                }
                return { success: true, action: params.action };
            }
        },
        {
            name: 'navigate',
            description: 'Navigates to a URL path within the application.',
            parameters: {
                type: 'object',
                properties: {
                    path: { type: 'string', description: 'The URL path to navigate to (e.g. /users or /users/123)' }
                },
                required: ['path']
            },
            execute: (params: Record<string, unknown>) => {
                getContext().page.current?.navigate(params.path as string);
                return { success: true, path: params.path };
            }
        },
        {
            name: 'list_entities',
            description: 'Lists entities from a model. Returns array of entities with their data.',
            parameters: {
                type: 'object',
                properties: {
                    model: { type: 'string', description: 'The model name (e.g. "users", "groups")' },
                    filter: { type: 'object', description: 'Optional filter object (e.g. { status: "active" })' }
                },
                required: ['model']
            },
            execute: async (params: Record<string, unknown>) => {
                const ctx = getContext();
                const auth = ctx.authn.find(a => a.capability?.model?.name === params.model);
                if (!auth?.capability?.model) {
                    const available = ctx.authn
                        .map(a => a.capability?.model?.name)
                        .filter(Boolean);
                    return { error: `Model '${params.model}' not found`, available };
                }
                const model = auth.capability.model as Model;
                const filter = params.filter as Record<string, unknown> | undefined;
                const entities = await ctx.database.list(model, ['*'], filter);
                return { model: params.model, count: entities.length, entities };
            }
        },
        {
            name: 'get_entity',
            description: 'Gets a single entity by ID from a model.',
            parameters: {
                type: 'object',
                properties: {
                    model: { type: 'string', description: 'The model name (e.g. "users", "groups")' },
                    id: { type: 'string', description: 'The entity ID' }
                },
                required: ['model', 'id']
            },
            execute: async (params: Record<string, unknown>) => {
                const ctx = getContext();
                const auth = ctx.authn.find(a => a.capability?.model?.name === params.model);
                if (!auth?.capability?.model) {
                    const available = ctx.authn
                        .map(a => a.capability?.model?.name)
                        .filter(Boolean);
                    return { error: `Model '${params.model}' not found`, available };
                }
                const model = auth.capability.model as Model;
                try {
                    const entity = await ctx.database.get(model, params.id as string, ['*']);
                    return { model: params.model, entity };
                } catch (err) {
                    return { error: err instanceof Error ? err.message : 'Entity not found' };
                }
            }
        },
        {
            name: 'describe_model',
            description: 'Gets the schema/structure of a model including its properties and actions.',
            parameters: {
                type: 'object',
                properties: {
                    model: { type: 'string', description: 'The model name (e.g. "users", "groups")' }
                },
                required: ['model']
            },
            execute: (params: Record<string, unknown>) => {
                const ctx = getContext();
                const auth = ctx.authn.find(a => a.capability?.model?.name === params.model);
                if (!auth?.capability?.model) {
                    const available = ctx.authn
                        .map(a => a.capability?.model?.name)
                        .filter(Boolean);
                    return { error: `Model '${params.model}' not found`, available };
                }
                const model = auth.capability.model as Model;
                return {
                    name: model.name,
                    singular: model.singular,
                    path: model.path,
                    type: model.type,
                    properties: model.properties.map(p => ({
                        name: p.name,
                        type: p.type,
                        description: p.description,
                        required: !!p.required,
                        reference: p.reference?.name
                    })),
                    actions: model.actions.map(a => a.name)
                };
            }
        }
    ];
}
